/**
 * ORCHID INSIGHTS.
 *
 * Seventeen headline metrics, a chart for any of them, seven breakdowns, and a
 * CSV. All of it computed in the database — this screen counts nothing, because
 * a dashboard whose totals are worked out in the browser is a dashboard whose
 * totals disagree with its own export the moment either one changes.
 *
 * WHAT THE SUPPRESSED ROWS ARE
 *
 * A breakdown cell built from fewer than five subjects comes back with no number
 * and a sentence saying why. That is script 161's small-cell rule and it is the
 * thing that makes the word "aggregate" true: "providers in the Northern Cape
 * offering tree felling: 1" is a named person wearing a hat, and Canon 4.4 lets
 * an aggregate export skip the time-boxed grant every other export needs.
 *
 * They are SHOWN rather than dropped, with the subject count beside them. A
 * reader who can see that a cell rests on three businesses reads it differently
 * from one who sees a gap and assumes a bug.
 *
 * THE CHART IS DRAWN AS AN SVG, BY HAND
 *
 * Seventeen points on one axis does not need a charting library, and the portal
 * ships to staff on office connections that are sometimes a phone hotspot. The
 * whole chart below is about forty lines and has no dependency.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { O, Card, StatusPill, LoadingPanel, Notice, PrimaryCTA, QuietButton, Field, textInputStyle } from '../ui';
import {
  fetchMetricCatalogue, fetchHeadline, fetchSeries, fetchBreakdown, fetchExport,
  DIMENSIONS, toCsv, csvFilename, movementLabel, movementTone, formatValue,
  fetchUnmetDemand,
} from '../lib/insights';

const eyebrow = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
  color: O.inkFaint, margin: '0 0 10px',
};

/**
 * One headline number, its movement, and what it counts.
 *
 * The movement carries a tone rather than an arrow glyph: an arrow is read as
 * direction and a colour is read as judgement, and these need judgement --
 * enquiries rising is a support load, not growth, which lib/insights encodes.
 */
const Tile = ({ row }) => (
  <div style={{
    border: `1px solid ${O.hairline}`, borderRadius: 12, padding: '13px 14px',
    background: '#FFFFFF',
  }}>
    <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: O.inkFaint }}>
      {row.metric}
    </p>
    <p style={{ margin: '7px 0 0', fontSize: 23, fontWeight: 700, color: O.ink,
                fontVariantNumeric: 'tabular-nums' }}>
      {formatValue(row.value, row.unit)}
    </p>
    <div style={{ marginTop: 7 }}>
      <StatusPill tone={movementTone(row.metric_key, row.movement)}>
        {movementLabel(row.movement)}
      </StatusPill>
    </div>
    <p style={{ margin: '7px 0 0', fontSize: 11.5, lineHeight: 1.6, color: O.inkMuted }}>
      {row.note}
    </p>
  </div>
);

/** A line, its area, and the last point marked. Nothing else. */
const Chart = ({ points, unit }) => {
  const W = 640;
  const H = 180;
  const PAD = 8;

  const { path, area, last } = useMemo(() => {
    const vals = points.map((p) => Number(p.value) || 0);
    const max = Math.max(...vals, 1);
    const step = points.length > 1 ? (W - PAD * 2) / (points.length - 1) : 0;
    const xy = vals.map((v, i) => [
      PAD + i * step,
      H - PAD - ((v / max) * (H - PAD * 2)),
    ]);
    const d = xy.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    return {
      path: d,
      area: `${d} L${(PAD + (points.length - 1) * step).toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`,
      last: xy[xy.length - 1],
    };
  }, [points]);

  if (!points.length) return null;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${points.length} months, ending ${points[points.length - 1].period_label} at ${formatValue(points[points.length - 1].value, unit)}`}
        style={{ width: '100%', minWidth: 420, height: 'auto', display: 'block' }}
      >
        <path d={area} fill={O.green} opacity="0.07" />
        <path d={path} fill="none" stroke={O.green} strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round" />
        {last && <circle cx={last[0]} cy={last[1]} r="4" fill={O.green} />}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontSize: 10.5, color: O.inkFaint }}>{points[0].period_label}</span>
        <span style={{ fontSize: 10.5, color: O.inkFaint }}>
          {points[points.length - 1].period_label}
        </span>
      </div>
    </div>
  );
};

const BreakdownTable = ({ rows }) => {
  if (!rows.length) return <p style={{ fontSize: 12.5, color: O.inkMuted }}>Nothing to slice yet.</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr>
            {['', 'Value', 'Subjects'].map((h) => (
              <th key={h} style={{
                textAlign: h === '' ? 'left' : 'right', padding: '6px 8px',
                borderBottom: `1px solid ${O.hairlineOn}`, color: O.inkFaint,
                fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td style={{ padding: '7px 8px', borderBottom: `1px solid ${O.hairline}`, color: O.ink }}>
                {row.bucket}
                {row.suppressed && (
                  <span style={{ marginLeft: 8 }}><StatusPill tone="quiet">Suppressed</StatusPill></span>
                )}
                <p style={{ margin: '2px 0 0', fontSize: 11, color: O.inkFaint }}>{row.note}</p>
              </td>
              <td style={{
                padding: '7px 8px', borderBottom: `1px solid ${O.hairline}`, textAlign: 'right',
                color: row.suppressed ? O.inkFaint : O.ink, fontVariantNumeric: 'tabular-nums',
                fontWeight: row.suppressed ? 400 : 600,
              }}>
                {row.suppressed ? '—' : `${Number(row.value).toLocaleString('en-ZA')} ${row.unit}`}
              </td>
              <td style={{
                padding: '7px 8px', borderBottom: `1px solid ${O.hairline}`, textAlign: 'right',
                color: O.inkMuted, fontVariantNumeric: 'tabular-nums',
              }}>
                {row.subjects}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function OrchidInsights() {
  const [catalogue, setCatalogue] = useState(null);
  const [headline, setHeadline] = useState(null);
  const [error, setError] = useState(null);

  const [metric, setMetric] = useState('jobs_started');
  const [months, setMonths] = useState(12);
  const [series, setSeries] = useState(null);

  const [dimension, setDimension] = useState('trade');
  const [breakdown, setBreakdown] = useState(null);

  // WHAT PEOPLE ASKED FOR AND ORCHID HAD NO TRADE FOR. Loaded with the page
  // rather than behind a button: it is the one thing on this screen that is a
  // decision waiting to be made rather than a number to be watched.
  const [unmet, setUnmet] = useState(null);

  const [exporting, setExporting] = useState(false);
  const [exportRows, setExportRows] = useState(null);
  const [exportError, setExportError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [cat, head, gaps] = await Promise.all([
        fetchMetricCatalogue(), fetchHeadline(), fetchUnmetDemand(null, 50),
      ]);
      if (!alive) return;
      if (head.error) { setError(head.error); setHeadline([]); return; }
      setCatalogue(cat.data || []);
      setHeadline(head.data || []);
      // A failure here is not a failure of the page. The tally is one section
      // of several and an empty one reads correctly as "nothing yet".
      setUnmet(gaps.error ? [] : (gaps.data || []));
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setSeries(null);
    fetchSeries(metric, months).then((res) => {
      if (!alive) return;
      setSeries(res.error ? [] : res.data);
    });
    return () => { alive = false; };
  }, [metric, months]);

  useEffect(() => {
    let alive = true;
    setBreakdown(null);
    fetchBreakdown(dimension).then((res) => {
      if (!alive) return;
      setBreakdown(res.error ? [] : res.data);
    });
    return () => { alive = false; };
  }, [dimension]);

  const unitOf = useCallback((key) => {
    const m = (catalogue || []).find((c) => c.metric_key === key);
    return m ? m.unit : 'count';
  }, [catalogue]);

  /**
   * Build the file, then hand it over as a link the person clicks themselves.
   *
   * Two steps rather than one, and the reason is not caution: the export is a
   * separate permission and a separate logged act, so a button that silently
   * downloaded on page load would be an export nobody asked for, recorded
   * against the name of somebody who only opened a dashboard.
   */
  const buildFile = async () => {
    setExporting(true);
    setExportError(null);
    const res = await fetchExport(months);
    setExporting(false);
    if (res.error) { setExportError(res.error); return; }
    setExportRows(res.data);
  };

  const href = useMemo(() => {
    if (!exportRows) return null;
    return `data:text/csv;charset=utf-8,${encodeURIComponent(toCsv(exportRows))}`;
  }, [exportRows]);

  if (headline === null && !error) return <LoadingPanel message="Counting the platform…" />;

  return (
    <>
      <h2 style={eyebrow}>Orchid Insights</h2>
      <p style={{ fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted, margin: '0 0 14px' }}>
        Every number here is computed in the database and names nobody. Any slice
        built from fewer than five subjects is shown without its value, because a
        count of one is a person rather than a statistic.
      </p>

      {error && <Notice tone="critical">{error}</Notice>}

      {headline && headline.length > 0 && (
        <div style={{
          display: 'grid', gap: 11, marginBottom: 18,
          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
        }}>
          {headline.map((row) => <Tile key={row.metric_key} row={row} />)}
        </div>
      )}

      <Card>
        <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: '0 0 12px' }}>
          Over time
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ flex: '1 1 240px' }}>
            <Field label="Metric" htmlFor="insight-metric">
              <select id="insight-metric" value={metric} style={textInputStyle(false)}
                      onChange={(e) => setMetric(e.target.value)}>
                {(catalogue || []).map((m) => (
                  <option key={m.metric_key} value={m.metric_key}>
                    {m.family} · {m.metric}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div style={{ flex: '0 1 140px' }}>
            <Field label="Months" htmlFor="insight-months">
              <select id="insight-months" value={months} style={textInputStyle(false)}
                      onChange={(e) => setMonths(Number(e.target.value))}>
                {[6, 12, 24, 36].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
          </div>
        </div>

        {series === null
          ? <p style={{ fontSize: 12.5, color: O.inkMuted }}>Reading…</p>
          : <Chart points={series} unit={unitOf(metric)} />}
      </Card>

      <div style={{ height: 14 }} />

      <Card>
        <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: '0 0 12px' }}>
          Sliced
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 12 }}>
          {DIMENSIONS.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => setDimension(d.key)}
              style={{
                border: `1px solid ${dimension === d.key ? O.green : O.hairlineOn}`,
                background: dimension === d.key ? O.green : '#FFFFFF',
                color: dimension === d.key ? '#FFFFFF' : O.inkMuted,
                borderRadius: 999, padding: '5px 12px', fontSize: 12, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {d.label}
            </button>
          ))}
        </div>
        {breakdown === null
          ? <p style={{ fontSize: 12.5, color: O.inkMuted }}>Reading…</p>
          : <BreakdownTable rows={breakdown} />}
      </Card>

      <div style={{ height: 14 }} />

      {/* ASKED FOR AND NOT OFFERED.
          A decision waiting to be made rather than a number to be watched, so
          it sits above the export rather than below it. */}
      <Card>
        <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: 0 }}>
          Asked for, and not offered
        </h3>
        <p style={{ margin: '7px 0 0', fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted }}>
          What people typed into Find a Provider that Orchid could not name a trade
          for. Grouped so one request said two ways counts once, and ranked by how
          many separate people asked rather than how many times it was typed —
          somebody retyping the same thing cannot climb this list.
        </p>
        <p style={{ margin: '7px 0 0', fontSize: 11.5, lineHeight: 1.7, color: O.inkFaint }}>
          Suppressed below the same floor every other cell on this page obeys —
          the number lives in the database, not here, so it cannot drift out of
          step with it. One person asking once is not demand, and a phrase too
          few people used never leaves the database at all.
        </p>

        {unmet === null ? (
          <p style={{ margin: '14px 0 0', fontSize: 12.5, color: O.inkFaint }}>Reading…</p>
        ) : unmet.length === 0 ? (
          // AN EMPTY LIST IS A GOOD RESULT AND IS SAID TO BE ONE. MDS 2.2's
          // rule about the Attention Centre applies here for the same reason:
          // a desk that reads "nothing" as "broken" goes looking for work that
          // does not exist.
          <p style={{ margin: '14px 0 0', fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted }}>
            Nothing has been asked for enough times to count as demand. Either the
            trades on Orchid cover what people are looking for, or too few people
            have asked for the same thing yet.
          </p>
        ) : (
          <div style={{ marginTop: 14, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr>
                  <th style={{ ...eyebrow, textAlign: 'left', padding: '0 0 8px' }}>What they asked for</th>
                  <th style={{ ...eyebrow, textAlign: 'right', padding: '0 0 8px' }}>People</th>
                  <th style={{ ...eyebrow, textAlign: 'right', padding: '0 0 8px' }}>Times</th>
                  <th style={{ ...eyebrow, textAlign: 'left', padding: '0 0 8px 16px' }}>Where</th>
                </tr>
              </thead>
              <tbody>
                {unmet.map((r) => (
                  <tr key={r.grouped_as} style={{ borderTop: `1px solid ${O.hairline}` }}>
                    <td style={{ padding: '10px 0', color: O.ink }}>
                      {/* THE PHRASES AS TYPED, which is the founder's ruling:
                          "yes, but logically Categorised and grouped." A desk
                          deciding whether to recruit a trade has to be able to
                          read the request. */}
                      {(r.phrases || []).slice(0, 3).join(' · ')}
                      {(r.phrases || []).length > 3 && (
                        <span style={{ color: O.inkFaint }}>
                          {' '}+{(r.phrases || []).length - 3} more wordings
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'right', color: O.ink,
                                 fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                      {r.askers}
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'right', color: O.inkMuted,
                                 fontVariantNumeric: 'tabular-nums' }}>
                      {r.times_asked}
                    </td>
                    <td style={{ padding: '10px 0 10px 16px', color: O.inkMuted }}>
                      {(r.provinces || []).length ? r.provinces.join(', ') : 'Not said'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div style={{ height: 14 }} />

      <Card>
        <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: 0 }}>
          Take it away
        </h3>
        <p style={{ margin: '7px 0 0', fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted }}>
          Every metric by month and every breakdown cell, in one CSV. This is a
          separate permission and a separate entry in the activity log, so the
          file is built when you ask for it rather than when the page opens.
          Suppressed cells export with an empty value and the word suppressed —
          never a zero, which a spreadsheet would add up.
        </p>

        {exportError && <div style={{ marginTop: 12 }}><Notice tone="warning">{exportError}</Notice></div>}

        <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <PrimaryCTA onClick={buildFile} busy={exporting} disabled={exporting}>
            {exporting ? 'Building…' : `Build the ${months}-month file`}
          </PrimaryCTA>
          {href && (
            <a
              href={href}
              download={csvFilename(months)}
              style={{
                fontSize: 13, fontWeight: 600, color: O.green, textDecoration: 'none',
                border: `1px solid ${O.green}`, borderRadius: 999, padding: '9px 16px',
              }}
            >
              Download {csvFilename(months)}
            </a>
          )}
          {exportRows && (
            <QuietButton onClick={() => setExportRows(null)} style={{ maxWidth: 150 }}>
              Discard
            </QuietButton>
          )}
        </div>

        {exportRows && (
          <p style={{ margin: '10px 0 0', fontSize: 11.5, color: O.inkFaint }}>
            {exportRows.length} row(s) ready.
          </p>
        )}
      </Card>
    </>
  );
}
